import type { ARCPattern, ARCPatternResult, LiveSnapshot } from "@/arc/arc.types";

export const permitDelaysPattern: ARCPattern = {
  id: "permit_delays",
  label: "Permit Delays",
  owner: "permits",
  urgency: "high",

  evaluate(snapshot: LiveSnapshot): ARCPatternResult {
    const { jobs } = snapshot;
    const openJobs = jobs.filter((j) => j.currentStage !== "Completed");

    const jobRev = (j: typeof jobs[0]) =>
      j.financials?.contractValue ?? j.payoutAmount ?? 0;

    const pending = openJobs.filter(
      (j) =>
        j.permitRequired &&
        j.permitStatus !== "Approved" &&
        j.permitStatus !== "Not Required"
    );

    const rejected = pending.filter(
      (j) => j.permitStatus === "Rejected" || j.permitStatus === "Expired"
    );

    const amountAtRisk = pending.reduce((s, j) => s + jobRev(j), 0);

    const summary = pending.map((j) => {
      const isRejected = j.permitStatus === "Rejected" || j.permitStatus === "Expired";
      return `${j.customerName} — ${j.permitStatus}${isRejected ? " ⚠" : ""}`;
    });

    return {
      triggered: pending.length > 0,
      count: pending.length,
      affectedJobIds: pending.map((j) => j.id),
      amountAtRisk,
      context:
        pending.length > 0
          ? `${pending.length} job${pending.length > 1 ? "s" : ""} with unresolved permits${rejected.length > 0 ? ` (${rejected.length} rejected/expired)` : ""} — $${amountAtRisk.toLocaleString()} stalled`
          : "All permits approved or not required",
      suggestedAction:
        rejected.length > 0
          ? "Resubmit rejected permits immediately — contact AHJ to expedite approval"
          : "Follow up on pending permits — confirm submission dates and expected turnaround",
      snapshot: {
        pendingJobIds: pending.map((j) => j.id),
        rejectedJobIds: rejected.map((j) => j.id),
        summary,
        amountAtRisk,
      },
    };
  },
};
